import * as api from '@/api/AccountApi';
import EventBus from '@/scripts/eventbus';
import { Notifications } from '@/scripts/notifications';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import moment from 'moment';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * User account definition
*/
export const useAccountStore = defineStore('AccountStore', () => {

    // Lock key w localStorage
    const REFRESH_LOCK_KEY = 'eps_client_refresh_token_lock';
    const REFRESH_UPDATED_KEY = 'eps_client_refresh_token_updated';
    const REFRESH_FAILED_KEY = 'eps_client_refresh_token_failed';

    let refreshPromise = null;

    // encryption key
    const encryptionKey = computed(() => process.env.VUE_APP_ENCRYPTION_KEY);

    // storage key
    const storageKey = computed(() => process.env.VUE_APP_STORAGE_AUTH_KEY);

    const userState = ref(getUser());

    // user account
    const user = computed(() => userState.value);

    function refreshUserState() {
        userState.value = getUser();
    }

    // user initials
    const initials = computed(() => {
        return getUser().name
            ?.match(/(^\S\S?|\s\S)?/g)
            ?.map(v => v.trim())
            ?.join("")
            ?.match(/(^\S|\S$)?/g)
            ?.join("")
            ?.toLocaleUpperCase()
    })

    // Check if token is still valid
    const isTokenValid = computed(() => {
        var user = getUser();
        if (!user.isAuthenticated) {
            return false;
        }

        var now = moment();
        var token = moment(user.refreshExpirationDate);

        if (token.diff(now) > 0) {
            return true;
        }

        return false;
    });

    const roles = computed(() => {
        return getUser().roles;
    })

    function delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function refreshAccessTokenShared() {
        if (refreshPromise) return refreshPromise;

        const now = Date.now();
        const lock = JSON.parse(localStorage.getItem(REFRESH_LOCK_KEY) || '{}');

        if (lock.timestamp && (now - lock.timestamp < 10000)) {
            return new Promise((resolve, reject) => {
                const onUpdate = (e) => {
                    if (e.key === REFRESH_UPDATED_KEY) {
                        window.removeEventListener('storage', onUpdate);
                        const data = JSON.parse(e.newValue);
                        if (data?.token) {
                            applyRemoteTokenUpdate(data);
                            resolve(data.token);
                        } else {
                            reject(new Error("Invalid token update from other tab."));
                        }
                    } else if (e.key === REFRESH_FAILED_KEY) {
                        window.removeEventListener('storage', onUpdate);
                        logout();
                        reject(new Error('Token refresh failed in another tab'));
                    }
                };
                window.addEventListener('storage', onUpdate);
            });
        }

        localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ timestamp: now }));

        refreshPromise = new Promise((resolve, reject) => {
            refreshUserToken(
                (response) => {
                    const updatedData = {
                        refreshExpirationDate: moment().add(response.data.refresh_expires_in, 'minutes').toISOString(),
                        accessExpirationDate: moment().add(response.data.expires_in, 'minutes').toISOString()
                    };

                    localStorage.setItem(REFRESH_UPDATED_KEY, JSON.stringify(updatedData));
                    localStorage.removeItem(REFRESH_UPDATED_KEY);
                    localStorage.removeItem(REFRESH_LOCK_KEY);

                    resolve(updatedData.token);
                },
                (err) => {
                    localStorage.setItem(REFRESH_FAILED_KEY, Date.now().toString());
                    localStorage.removeItem(REFRESH_LOCK_KEY);
                    reject(err);
                }).finally(() => {
                    refreshPromise = null;
                });
        });

        return refreshPromise;
    }

    // get encoded data from storage
    function getFromStorage() {
        return localStorage.getItem(storageKey.value);
    }

    // set encoded data to storage
    function setToStorage(value) {
        localStorage.setItem(storageKey.value, value);
    }

    // delete data from storage
    function deleteFromStorage() {
        localStorage.removeItem(storageKey.value);
    }

    // encrypt json
    function encrypt(value) {
        return CryptoJS.AES.encrypt(value, encryptionKey.value).toString();
    }

    // decrypt to json
    function decrypt(value) {
        if (value == null)
            return null;

        try {
            var result = CryptoJS.AES.decrypt(value, encryptionKey.value).toString(CryptoJS.enc.Utf8);
            return result;
        } catch (exception) { }

        return null;
    }

    /**
     * Get user from storage
     */
    function getUser() {
        var encryptedValue = getFromStorage();
        var value = decrypt(encryptedValue);
        if (value == null)
            return {
                isAuthenticated: false
            };



        return JSON.parse(value);
    }

    /**
     * Save user in storage
     * @param {any} value
     */
    function saveUser(value) {
        var encryptedValue = encrypt(JSON.stringify(value));
        setToStorage(encryptedValue);
    }

    /**
     * Save refresh token from in-memory to storage
     * */
    function beforeUnload() {
        const currentUser = getUser();
        if (!currentUser?.isAuthenticated) return;

        const copy = { ...currentUser };

        const encrypted = encrypt(JSON.stringify(copy));
        setToStorage(encrypted);
    }

    /**
     * Refresh user token using storage temporary token
     * @param {any} success
     * @param {any} fail
     */
    function afterPageLoad() {
        const currentUser = getUser();

        if (currentUser) {
            const userCopy = { ...currentUser };

            const encrypted = encrypt(JSON.stringify(userCopy));
            setToStorage(encrypted);

            setTimeout(() => {
                const stored = getUser();
                setToStorage(encrypt(JSON.stringify(stored)));
            }, 500);
        }
    }

    /**
     * Login user
     * @param {any} username - username
     * @param {any} password - password
     * @param {any} success - success func
     * @param {any} fail - fail func
     */
    function login(username, password, success, fail) {
        api.login(
            username,
            password,
            (response) => {
                var value = {
                    isAuthenticated: true,
                    name: response.data.fullName,
                    login: response.data.userName,
                    roles: response.data.userRoles,
                    refreshExpirationDate: moment().add(response.data.refresh_expires_in, 'minutes'),
                    accessExpirationDate: moment().add(response.data.expires_in, 'minutes')
                };

                saveUser(value);
                success && success(response);
            },
            (response) => {
                fail && fail(response);
            }
        );
    };

    /**
     * Logout user
     */
    function logout() {
        deleteFromStorage();
    }

    /**
     * Refresh user token using refresh token
     * @param {any} currentRefreshToken
     * @param {any} success
     * @param {any} fail
     */
    function refreshUserToken(success, fail) {
        return api.refreshToken(getUser().login,
            (response) => {
                var value = {
                    isAuthenticated: true,
                    name: response.data.fullName,
                    login: response.data.userName,
                    roles: response.data.userRoles,
                    refreshExpirationDate: moment().add(response.data.refresh_expires_in, 'minutes').toISOString(),
                    accessExpirationDate: moment().add(response.data.expires_in, 'minutes').toISOString()
                };

                saveUser(value);
                refreshUserState();
                EventBus.emit('tokenRefreshed');
                success && success(response);
            },
            (response) => {
                fail && fail(response);
            })
    }

    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
        failedQueue.forEach(prom => {
            if (error)
                prom.reject(error);
            else
                prom.resolve(token);
        });

        failedQueue = [];
    };

    /**
     * Create AXIOS authorization header
     * and refresh token after expired
     * */
    function setAxiosAuthorization() {

        // set authrization header
        axios.interceptors.request.use(
            config => {
                config.withCredentials = true;
                return config;
            });

        // we want to handle error status on interceptor response
        axios.interceptors.response.use(function (response) {
            return response;
        }, function (error) {

            const originalRequest = error.config;

            // if 440 - session expired - timeout 
            if (error.response.status == 440) {
                logout();
                window.location = "/login?url=" + window.location.pathname + window.location.search;
                return Promise.reject(response);
            }

            // Reject promise if usual error
            if (error.response.status === 401 && !originalRequest._retry) {

                if (isRefreshing) {
                    return new Promise(function (resolve, reject) {
                        failedQueue.push({ resolve, reject })
                    }).then(token => {
                        //originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return axios(originalRequest);
                    }).catch(err => {
                        logout();
                        window.location = "/login?url=" + window.location.pathname + window.location.search;
                        return Promise.reject(err);
                    })
                }

                originalRequest._retry = true;
                isRefreshing = true;

                return new Promise(function (resolve, reject) {
                    refreshAccessTokenShared()
                        .then((token) => {
                            //originalRequest.headers['Authorization'] = 'Bearer ' + token;
                            processQueue(null, token);
                            resolve(axios(originalRequest));
                        })
                        .catch((err) => {
                            processQueue(err, null);
                            logout();
                            window.location = "/login?url=" + window.location.pathname + window.location.search;
                            reject(err);
                        }).finally(() => { isRefreshing = false; setAxiosAuthorization(); });
                })
            };

            if (error?.response?.status === 403) {
                Notifications.showForbiddenMessage();
            };

            return Promise.reject(error);
        });

        window.addEventListener('storage', (e) => {
            if (e.key === REFRESH_UPDATED_KEY && e.newValue) {
                try {
                    const payload = JSON.parse(e.newValue);
                    if (payload?.token) {
                        applyRemoteTokenUpdate(payload);
                    }
                } catch (_) { }
            }
        });
    }

    /**
     * Get user permissions
     * @param {any} success - success func
     * @param {any} fail - fail func
     */
    function getPermissions(success, fail) {
        api.getPermissions(
            (response) => {
                success && success(response);
            },
            (response) => {
                fail && fail(response);
            }
        );
    };

    function applyRemoteTokenUpdate(payload) {
        if (!payload?.token || !payload?.refreshExpirationDate) return;

        const currentUser = getUser();
        const updated = {
            ...currentUser,
            token: payload.token,
            refreshExpirationDate: payload.refreshExpirationDate,
            accessExpirationDate: payload.accessExpirationDate
        };

        saveUser(updated);
        refreshUserState();
        EventBus.emit('tokenRefreshed');
    }

    function applyRemoteUserUpdate(payload) {
        if (!payload) return;

        saveUser(payload);
        refreshUserState();
    }

    function getJwtExpiry(token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp * 1000; //ms
        } catch (e) {
            return null;
        }
    };

    /**
     * Login user
     * @param {any} success - success func
     * @param {any} fail - fail func
     */
    function finishSession(success, fail) {
        api.finishSession(
            (response) => {
                success && success(response);
            },
            (response) => {
                fail && fail(response);
            }
        );
    };


    /**
    * Send ForgotPassword to server 
    * @param {model} model 
    * @param {function} success 
    * @param {function} fail 
    */
    function forgotPassword(model, success, fail) {
        api.forgotPassword(model,
            (response) => { success && success(response); },
            (response) => { fail && fail(response); })
    };

    /**
     * Send ForgotPassword to server 
     * @param {model} model 
     * @param {function} success 
     * @param {function} fail 
     */
    function sendSMSCode(model, success, fail) {
        api.sendSMSCode(model,
            (response) => { success && success(response); },
            (response) => { fail && fail(response); })
    };

    /**
     * Send Reset password to server
     * @param {model} model 
     * @param {function} success 
     * @param {function} fail 
     */
    function resetPassword(model, success, fail) {
        api.resetPassword(model,
            (response) => { success && success(response); },
            (response) => { fail && fail(response); })
    };

    /**
     * Change password on the server
     * @param {model} model 
     * @param {function} success 
     * @param {function} fail 
     */
    function changePassword(model, success, fail) {
        api.changePassword(model,
            (response) => { success && success(response); },
            (response) => { fail && fail(response); })
    };

    return {

        // computed
        user, initials, roles, isTokenValid,

        // methods
        login, logout,
        beforeUnload, afterPageLoad,
        setAxiosAuthorization, getPermissions,

        // api
        forgotPassword, resetPassword, changePassword,
        sendSMSCode, finishSession
    };
});